import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
// utils
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

export default function StopDialog({ jobPost, isOpen, reloadJobs, closeDialog }) {
  const { user } = useAuth();
  const smUp = useResponsive("up", "sm");
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = useState(false);
  const [step, setStep] = useState("initiate"); // stopmore, stopall

  const stopAll = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    try {
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/stop/`, {});
      setIsSubmit(false);
      enqueueSnackbar('공고를 취소하였습니다.');
      await reloadJobs();
      closeDialog();
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('공고 취소에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  };

  const stopMore = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    try {
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/stop-more/`);
      setIsSubmit(false);
      enqueueSnackbar('추가 매칭을 중단하였습니다.');
      await reloadJobs();
      closeDialog();
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('추가 매칭 중단에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStep("initiate");
    }
  }, [isOpen]);

  const checkAndNextStopMore = () => {
    if (jobPost?.candidate_set?.length === 0) {
      enqueueSnackbar('매칭된 후보가 없습니다. 공고 취소만 가능합니다.', { variant: 'error' });
    } else {
      setStep("stopmore");
    }
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="dg">
      {step === "initiate" && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
        <Typography variant="h5" sx={{ mb: { xs: 2, sm: 3 } }}>
          공고 중단하기
        </Typography>
        <Stack spacing={2}>
          <Stack
            sx={{
              borderRadius: 1,
              p: 2,
              "&:hover": {background: (theme) => theme.palette.grey[200], cursor: "pointer"},
              border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
            }}
            direction="row"
            spacing={1}
            alignItems={"flex-start"}
            onClick={() => { checkAndNextStopMore(); } }
          >
            <SvgIconStyle src="/icons/icon-user-check-mono.svg" sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20 }} />
            <Stack spacing={.5}>
              <Typography variant="subtitle2">
                후보를 충분히 제안 받았습니다. 추가 제안은 받지 않겠습니다.
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                현재까지 매칭된 후보들과 미팅이나 계약을 진행하실 수 있습니다.
              </Typography>
            </Stack>
          </Stack>

          <Stack
            sx={{
              borderRadius: 1,
              p: 2,
              "&:hover": {background: (theme) => theme.palette.grey[200], cursor: "pointer"},
              border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
            }}
            direction="row"
            spacing={1}
            alignItems={"flex-start"}
            onClick={() => setStep("stopall")}
          >
            <SvgIconStyle src="/icons/icon-minus-circle-mono.svg" sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20 }} />
            <Stack spacing={.5}>
              <Typography variant="subtitle2">
                공고를 아예 취소해주세요.
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                매칭된 후보들과 미팅이나 계약을 진행할 수 없고, 예약된 미팅은 모두 취소됩니다.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      )}
      {step === "stopmore" && (
        <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
          <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
          <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
            추가 매칭을 중단합니다.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
            현재까지 매칭된 후보들과 미팅이나 계약을 진행하실 수 있습니다.
          </Typography>
        </DialogContent>
      )}
      {step === "stopall" && (
        <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
          <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
          <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
            공고를 중단하면, 예약된 미팅은 모두 취소됩니다.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
            매칭된 후보들과 미팅이나 계약을 진행할 수 없고, 예약된 미팅은 모두 취소됩니다.
          </Typography>
        </DialogContent>
      )}
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={smUp ? "row" : "column-reverse"} spacing={2} sx={{ width: 1 }} justifyContent="flex-end">
        <LoadingButton
          onClick={closeDialog}
          loading={isSubmit}
          size="large"
          variant={step === "initiate" ? "contained" : "text"}
          sx={{ px: 6 }}
        >
          닫기
        </LoadingButton>
        {step === "stopmore" && (
          <LoadingButton
            onClick={stopMore}
            loading={isSubmit}
            size="large"
            variant="contained"
          >
            추가 매칭 중단하기
          </LoadingButton>
        )}
        {step === "stopall" && (
          <LoadingButton
            onClick={stopAll}
            loading={isSubmit}
            size="large"
            variant="contained"
          >
            공고 중단하기
          </LoadingButton>
        )}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
